import axios, { AxiosPromise, AxiosResponse } from "axios";
import {
  IAttribute,
  IConfiguration,
  IContainer,
  IDictionary,
  IOption,
  ITemplate,
  IUploadYourLabel,
} from "../../typings";
// @ts-ignore
import { changeDpiDataUrl } from "changedpi";
import IEventController from "./ievent-controller";
import { ISize } from "./size-controller";
import * as optionValidators from "./option-validators";

export default class ExternalController {
  main: IContainer<IConfiguration>;
  outsideClass: IUploadYourLabel;
  _onChangeCallbacks: Array<Function>;
  private callback?: (json: string) => void;
  private renderImage: boolean = false;
  private imageEvent: (element: CustomEvent) => void;
  private previewEvent: (element: CustomEvent) => void;
  private eventController: IEventController;
  private convertImagePromises: IDictionary<IDictionary<any>> = {};

  constructor(
    main: IContainer<IConfiguration>,
    eventController: IEventController,
  ) {
    this.main = main;
    this.eventController = eventController;

    this._onChangeCallbacks = [];

    this.exportAsJson = this.exportAsJson.bind(this);
    this._setOutsideClass = this._setOutsideClass.bind(this);
  }

  _setOutsideClass(cls: IUploadYourLabel): void {
    this.outsideClass = cls;
  }

  exportAsJson(): string {
    const output: any =
      this.main.outputController.storageController.getOutputTemplate();
    return JSON.stringify(output);
  }

  uploadImageAndExportAsJson(
    callback: (json: string) => void,
    server: boolean = false,
  ): Promise<void> {
    if (typeof this.callback !== "undefined") {
      return;
    }
    if (!this.main.storageController.getUpdated() && !server) {
      callback(this.exportAsJson());
      return;
    }

    this.callback = callback;
    const target: IEventController = this.eventController;

    const promises: Promise<unknown>[] = [];
    const imageAttrs: IDictionary<IAttribute> =
      this.main.templateController.getOptionByCode("image").attributes;
    const imageBackAttrs: IDictionary<IAttribute> =
      this.main.templateController.getOptionByCode("image").attributes;

    let imageDpi: number;
    if (imageAttrs.dpi) {
      imageDpi = imageAttrs.dpi.value;
    } else {
      const innerSize: ISize = this.main.sizeController.getInnerSize();
      const croppedData: Cropper.Data = this.main.outputController.getValue([
        "image",
        "cropped-data",
      ]);
      imageDpi = croppedData.width / (innerSize.width / 25.4);
    }

    let imageBackDpi: number;
    if (imageBackAttrs) {
      if (imageBackAttrs.dpi) {
        imageBackDpi = imageBackAttrs.dpi.value;
      } else {
        const innerSize: ISize = this.main.sizeController
          .getChild("original-back")
          .getInnerSize();
        const croppedData: Cropper.Data = this.main.outputController.getValue([
          "image",
          "cropped-data-back",
        ]);
        if (croppedData) {
          imageBackDpi = croppedData.width / (innerSize.width / 25.4);
        }
      }
    }

    const template: IAttribute = imageAttrs.template;
    this.renderImage = true;
    if (
      server &&
      typeof template === "object" &&
      template.value === "image-renderer"
    ) {
      promises.push(
        new Promise((resolve: Function, reject: () => void) => {
          this.imageEvent = (element: CustomEvent): void => {
            this.uploadImage(changeDpiDataUrl(element.detail, imageDpi))
              .then((url: string) => {
                this.main.outputController
                  .setValue(["image", "image"], url)
                  .then(() => {})
                  .catch(() => {});
                target.removeEventListener("image_rendered", this.imageEvent);
                resolve();
              })
              .catch(reject);
          };
          target.addEventListener("image_rendered", this.imageEvent);
        }),
      );
      target.dispatchEvent(this.eventController.newEvent("render_image"));
    }

    const fileBack: string = this.main.outputController.getValue([
      "image",
      "file-back",
    ]);
    const originalBack: string = this.main.outputController.getValue([
      "image",
      "original-back",
    ]);
    const templateBack: IOption =
      this.main.templateController.getOptionByCode("image-back");
    if (
      server &&
      fileBack === "upload-backside" &&
      originalBack &&
      templateBack &&
      typeof templateBack.attributes.template === "object" &&
      templateBack.attributes.template.value === "image-renderer"
    ) {
      promises.push(
        new Promise((resolve: Function, reject: () => void) => {
          this.imageEvent = (element: CustomEvent): void => {
            this.uploadImage(changeDpiDataUrl(element.detail, imageBackDpi))
              .then((url: string) => {
                this.main.outputController
                  .setValue(["image", "image-back"], url)
                  .then(() => {})
                  .catch(() => {});
                target.removeEventListener(
                  "image_back_rendered",
                  this.imageEvent,
                );
                resolve();
              })
              .catch(reject);
          };
          target.addEventListener("image_back_rendered", this.imageEvent);
        }),
      );
      target.dispatchEvent(this.eventController.newEvent("render_image_back"));
    }

    promises.push(
      new Promise((resolve: Function, reject: () => void) => {
        this.previewEvent = (element: CustomEvent): void => {
          if (!server) {
            this.main.outputController
              .setValue(["image", "preview"], element.detail)
              .then(() => {})
              .catch(() => {});
            resolve();
            target.removeEventListener("preview_rendered", this.previewEvent);
            return;
          }

          this.uploadImage(changeDpiDataUrl(element.detail, 300))
            .then((url: string) => {
              this.main.outputController
                .setValue(["image", "preview"], url)
                .then(() => {})
                .catch(() => {});
              resolve();
              target.removeEventListener("preview_rendered", this.previewEvent);
            })
            .catch(reject);
        };
        target.addEventListener("preview_rendered", this.previewEvent);
      }),
    );
    target.dispatchEvent(this.eventController.newEvent("render_preview"));

    return Promise.all(promises).then(() => {
      const output: ITemplate = this.main.outputController.getOutputTemplate();
      this.callback(JSON.stringify(output));
      this.callback = undefined;
    });
  }

  uploadImage(url: string): Promise<unknown> {
    if (typeof this.main.conf.upload_address !== "string") {
      return new Promise<unknown>((resolve: Function) => resolve(url));
    }

    return new Promise((resolve: Function, reject: (err: any) => void) => {
      axios
        .post(
          this.main.conf.upload_address,
          { base64File: url.replace(/^data:(.*;base64,)?/, "") },
          {
            headers: {
              "Content-Type": "application/json",
            },
          },
        )
        .then((response: { data: { url: string } }) => response.data)
        .then((data: { url: string }) => resolve(data.url))
        .catch((err: any) => reject(err));
    });
  }

  convertImage(importUrl: string, filename?: string): Promise<unknown> {
    if (typeof this.main.conf.convertAddress !== "string") {
      return new Promise<unknown>((resolve: Function) => resolve(importUrl));
    }

    if (
      this.convertImagePromises[importUrl] &&
      this.convertImagePromises[importUrl][filename]
    ) {
      return this.convertImagePromises[importUrl][filename];
    }

    if (!this.convertImagePromises[importUrl]) {
      this.convertImagePromises[importUrl] = {};
    }

    if (this.convertImagePromises[importUrl][filename] === false) {
      throw new Error();
    }

    const promise: AxiosPromise<any> = axios
      .post(
        this.main.conf.convertAddress,
        { importUrl, filename },
        {
          headers: {
            Origin: "https://www.dutchlabelshop.com",
            Referer: "https://www.dutchlabelshop.com",
            "Content-Type": "application/json",
          },
        },
      )
      .catch((err: any) => {
        throw err;
      });
    this.convertImagePromises[importUrl][filename] = promise;

    return promise
      .then((response: any) => {
        if (!response.data.success) {
          throw response.error;
        }
        return response.data.data;
      })
      .then((data: { url: { url: string } }) => {
        this.convertImagePromises[importUrl][filename] = false;
        return data.url.url;
      });
  }

  addOnChangeCallback(callback: Function): void {
    this._onChangeCallbacks.push(callback);
  }

  _triggerOnChangeEvents(type: Array<string>, value: any, oldValue: any): void {
    if (!this.main.storageController.getUpdated()) {
      this.main.storageController.setUpdated(true);
    }
    if (this._onChangeCallbacks) {
      if (this._onChangeCallbacks.length > 0) {
        this._onChangeCallbacks.forEach((callback: Function) => {
          callback(type, value, oldValue, this.outsideClass);
        });
      }
    }
  }

  clearCache(): void {
    this.main.storageController.clearCache();
  }

  getSizeOfImage(url: string): Promise<number> {
    return axios
      .get(url)
      .then(
        (response: AxiosResponse<any>): number =>
          response.headers["content-length"],
      );
  }

  isInputValid(): void {
    const output: any = this.main.storageController.getOutputTemplate();
    const template: any = this.main.templateController.getTemplate();

    if (template === null) {
      return;
    }

    Object.keys(template.options).forEach((optionKey: string) => {
      if (!optionValidators.checkTextHasNoEmoji(template, output, optionKey)) {
        throw {
          optionKey,
          message: template.meta.fields.validationErrorTextCanNotContainEmoji,
        };
      }
    });
  }

  setMessage(message: string): void {
    this.outsideClass.setMessage(message);
  }
}
